import { inject } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { AtsRepository } from "network/ats-repository";
import { ContactsRepository } from "network/contacts-repository";
import { AtsAppMetrics, AtsOrgMetrics } from "models/ats-metrics";
import { ContactsAppMetrics } from "models/contacts-metrics";

export type LoadingStatus = "loading" | "loaded" | "error";

interface AppMetricsShape {
    atsOrganizationId: string;
    ats: AtsAppMetrics;
    org: AtsOrgMetrics;
    contacts: ContactsAppMetrics;
    lastLoaded: Date;
    status: LoadingStatus;
}

export class AppMetricsEnsureAction {
    constructor(public atsOrganizationId: string = null) {}
}

export class AppMetricsRefreshAction {}
export class AppMetricsResetAction {}

@inject
export class AppMetricsStore extends Store<AppMetricsShape> {
    constructor(
        private atsRepo: AtsRepository,
        private contactsRepo: ContactsRepository
    ) {
        super();
    }

    defaultState(): AppMetricsShape {
        return {
            atsOrganizationId: null,
            ats: null,
            org: null,
            contacts: null,
            lastLoaded: null,
            status: null
        };
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.handleResetAction();
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.handleResetAction();
    }

    @handle(AppMetricsResetAction)
    private handleResetAction() {
        this.setState(() => this.defaultState());
    }

    @handle(AppMetricsEnsureAction)
    private async handleEnsureAction(action: AppMetricsEnsureAction) {
        const { atsOrganizationId, lastLoaded, status } = this.state;
        if (action.atsOrganizationId === atsOrganizationId && status === "loaded") {
            // Reload the state store if the data is older than 5 minutes.
            const fiveMinutesAgo = new Date();
            fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);

            if (lastLoaded > fiveMinutesAgo) {
                return;
            }
        }

        this.setState(() => ({
            ...this.defaultState(),
            status: "loading",
            atsOrganizationId: action.atsOrganizationId
        }));

        await this.loadData();
    }

    @handle(AppMetricsRefreshAction)
    private async handleRefreshAction() {
        if (this.state.status === "loading") {
            return;
        }

        this.setState((state) => ({
            ...state,
            status: "loading"
        }));

        await this.loadData();
    }

    private async loadData() {
        const { atsOrganizationId } = this.state;

        try {
            const atsMetrics = await this.atsRepo.appMetrics(atsOrganizationId);
            const orgMetrics = await this.atsRepo.orgMetrics(atsOrganizationId);
            const contactsMetrics = await this.contactsRepo.appMetrics(atsOrganizationId);

            this.setState((state) => ({
                ...state,
                ats: atsMetrics,
                org: orgMetrics,
                contacts: contactsMetrics,
                lastLoaded: new Date(),
                status: "loaded"
            }));
        } catch (err) {
            this.setState((state) => ({
                ...state,
                ats: null,
                org: null,
                contacts: null,
                status: "error"
            }));
        }
    }
}
